import { useEffect, useState } from 'react';
import {
    EyeIcon, XMarkIcon, CheckCircleIcon, CalendarDaysIcon, CakeIcon,
} from '@heroicons/react/24/outline';
import StatusBadge from '../StatusBadge';
import { useCakeOrders } from '../../hooks/useOrders';
import { STATUS_OPTIONS, ORDER_STATUS } from '../../constants/orderStatuses';
import type { CakeOrderWithOrder } from '../../types';

// ── Constants ─────────────────────────────────────────────────────────────────

const FILTER_OPTIONS = [{ value: 'all', label: 'All statuses' }, ...STATUS_OPTIONS];

const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });

// ── Component ─────────────────────────────────────────────────────────────────

export default function CakeOrdersTab() {
    const { orders, loading, fetchOrders, updateStatus } = useCakeOrders();

    const [statusFilter,   setStatusFilter]   = useState('all');
    const [search,         setSearch]         = useState('');
    const [selectedOrder,  setSelectedOrder]  = useState<CakeOrderWithOrder | null>(null);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [newStatus,      setNewStatus]      = useState('');
    const [updateSuccess,  setUpdateSuccess]  = useState('');

    useEffect(() => { void fetchOrders(); }, [fetchOrders]);

    const filtered = orders.filter(o => {
        if (statusFilter !== 'all' && o.order?.status?.toUpperCase() !== statusFilter) return false;
        if (search.trim()) {
            const q    = search.toLowerCase();
            const ref  = o.order?.reference?.toLowerCase() ?? '';
            const name = `${o.order?.customer?.first_name ?? ''} ${o.order?.customer?.last_name ?? ''}`.toLowerCase();
            if (!ref.includes(q) && !name.includes(q)) return false;
        }
        return true;
    });

    const openDetail = (o: CakeOrderWithOrder) => {
        setSelectedOrder(o);
        setNewStatus(o.order?.status ?? ORDER_STATUS.CREATED);
        setUpdateSuccess('');
    };

    const handleUpdateStatus = async () => {
        if (!selectedOrder?.order) return;
        setUpdatingStatus(true);
        const ok = await updateStatus(selectedOrder.order_id, newStatus);
        if (ok) {
            setUpdateSuccess('Status updated!');
            setTimeout(() => setUpdateSuccess(''), 2000);
        }
        setUpdatingStatus(false);
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-5">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <input type="text" placeholder="Search by order ref or customer name…"
                       value={search} onChange={e => setSearch(e.target.value)}
                       className="flex-1 rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#ee2b74]" />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                        className="rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ee2b74]">
                    {FILTER_OPTIONS.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                </select>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-2 text-sm text-stone-500">
                <CakeIcon className="h-4 w-4 text-[#ee2b74]" />
                <span>{orders.length} total cake order{orders.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Table */}
            {loading ? (
                <div className="animate-pulse space-y-3 py-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="h-10 rounded bg-stone-100" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="py-12 text-center text-sm text-stone-400">
                    {orders.length === 0 ? 'No cake orders yet.' : 'No cake orders match your filters.'}
                </div>
            ) : (
                <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b border-stone-200 bg-stone-50">
                            <tr>
                                {['Order Ref', 'Customer', 'Size', 'Collection Date', 'Total', 'Status', ''].map(h => (
                                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                                ))}
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                            {filtered.map(o => (
                                <tr key={o.id} className="hover:bg-stone-50 transition-colors">
                                    <td className="px-4 py-3 font-medium text-stone-900">
                                        {o.order?.reference ?? o.order_id.substring(0, 8).toUpperCase()}
                                    </td>
                                    <td className="px-4 py-3 text-stone-700">
                                        {o.order?.customer
                                            ? `${o.order.customer.first_name} ${o.order.customer.last_name}`
                                            : '—'}
                                        {o.order?.customer?.cellphone && (
                                            <p className="text-xs text-stone-400">{o.order.customer.cellphone}</p>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-stone-600">{o.selection?.size_label ?? '—'}</td>
                                    <td className="px-4 py-3 text-stone-600">
                                        <div className="flex items-center gap-1.5">
                                            <CalendarDaysIcon className="h-3.5 w-3.5 text-stone-400" />
                                            {formatDate(o.collection_date)}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 font-medium text-stone-900">
                                        R{(o.total_price / 100).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <StatusBadge status={o.order?.status ?? ''} />
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button onClick={() => openDetail(o)}
                                                className="rounded-md p-1.5 text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors">
                                            <EyeIcon className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Detail modal */}
            {selectedOrder && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/60 backdrop-blur-[1px]">
                    <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl ring-1 ring-stone-200 overflow-hidden max-h-[90vh] overflow-y-auto">
                        <div className="h-1 w-full bg-[#ee2b74]" />
                        <div className="p-6 space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold text-stone-900">Cake Order Detail</h3>
                                    <p className="text-xs text-stone-500 mt-0.5">{selectedOrder.order?.reference}</p>
                                </div>
                                <button onClick={() => setSelectedOrder(null)}
                                        className="rounded-lg p-1 text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors">
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>

                            {selectedOrder.order?.customer && (
                                <div className="rounded-lg border border-stone-200 p-4">
                                    <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Customer</p>
                                    <p className="font-medium text-stone-900">
                                        {selectedOrder.order.customer.first_name} {selectedOrder.order.customer.last_name}
                                    </p>
                                    <p className="text-sm text-stone-500">{selectedOrder.order.customer.cellphone}</p>
                                </div>
                            )}

                            <div className="rounded-lg border border-stone-200 bg-stone-50 p-4">
                                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Cake Details</p>
                                <div className="space-y-2">
                                    {selectedOrder.line_items?.map((item, i) => (
                                        <div key={i} className="flex justify-between text-sm">
                                            <span className={i === 0 ? 'font-medium text-stone-900' : 'text-stone-600'}>{item.label}</span>
                                            <span className={i === 0 ? 'font-medium text-stone-900' : 'text-stone-600'}>
                                                {i === 0 ? `R${(item.price / 100).toFixed(2)}` : `+R${(item.price / 100).toFixed(2)}`}
                                            </span>
                                        </div>
                                    ))}
                                    <div className="pt-2 border-t border-stone-200 flex justify-between font-semibold text-stone-900">
                                        <span>Total</span>
                                        <span>R{(selectedOrder.total_price / 100).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 text-sm text-stone-600">
                                <CalendarDaysIcon className="h-4 w-4 text-stone-400" />
                                <span>Collection: <strong>{formatDate(selectedOrder.collection_date)}</strong></span>
                            </div>

                            {selectedOrder.selection?.cake_message && (
                                <div className="rounded-lg border border-stone-200 p-3 text-sm text-stone-600 italic">
                                    🎂 "{String(selectedOrder.selection.cake_message)}"
                                </div>
                            )}

                            {selectedOrder.special_notes && (
                                <div className="rounded-lg border border-stone-200 p-3 text-sm text-stone-600">
                                    <p className="text-xs font-semibold text-stone-400 uppercase mb-1">Special Notes</p>
                                    {selectedOrder.special_notes}
                                </div>
                            )}

                            <div className="border-t border-stone-100 pt-4">
                                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">Update Status</p>
                                <div className="flex gap-2">
                                    <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
                                            className="flex-1 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ee2b74]">
                                        {STATUS_OPTIONS.map(s => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                    <button onClick={handleUpdateStatus}
                                            disabled={updatingStatus || newStatus === selectedOrder.order?.status}
                                            className="px-4 py-2 rounded-lg bg-[#ee2b74] hover:bg-[#d4245f] text-white text-sm font-medium disabled:opacity-50 transition-colors">
                                        {updatingStatus ? 'Saving…' : 'Update'}
                                    </button>
                                </div>
                                {updateSuccess && (
                                    <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700">
                                        <CheckCircleIcon className="h-4 w-4" />{updateSuccess}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}